import React, { useCallback, useEffect, useState, ReactNode, Dispatch, SetStateAction } from "react";
import { useTheme, lighten, Typography, SxProps, Box, SvgIcon, Tooltip, TooltipProps, styled, tooltipClasses, Grid, Badge, BadgeProps } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import { DisplayZoneValue, Partition, ZonesPlistValue } from "./types";
import { Checkbox, CheckboxProps } from "@mb-pro-ui/components";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import SelectedZones from "./SelectedZones";
import SelectedPartitions from "./SelectedPartitions";
import { behaviors, triggers } from "./SmartalarmBoard";
import ZoneChip from "../ZoneView";

import { ReactComponent as StayModeIcon } from "./remotecontrol/button3.svg";

export interface PZConfiguratorValue {
  [zoneId: string]: {
    [partitionId: string]: boolean;
  };
}

interface PZConfiguratorProps {
  value: PZConfiguratorValue | null;
  matrix: MatrixValue[][] | null;
  setMatrix: Dispatch<SetStateAction<MatrixValue[][] | null>>;
  saved: ZonesPlistValue | null;
  zones: DisplayZoneValue[];
  partitions: Partition[];
  view: "grid" | "row";
  switchPartitionsAndZones: boolean;
  onChange: (value: MatrixValue) => void;
  onAllChange?: (value: MatrixValue[][]) => void;
  disabled?: boolean;
}

export interface MatrixValue {
  zId: string;
  pId: string;
  value: boolean;
}

interface ViewProps {
  value: MatrixValue[][];
  saved: ZonesPlistValue | null;
  zones: DisplayZoneValue[];
  partitions: Partition[];
  switchPartitionsAndZones: boolean;
  onChange: (value: MatrixValue) => void;
  onAllChange?: (value: MatrixValue[][]) => void;
  disabled?: boolean;
}

const PZConfigurator = ({
  value,
  matrix,
  setMatrix,
  saved,
  partitions,
  zones,
  view,
  switchPartitionsAndZones,
  disabled,
  onChange,
  onAllChange,
}: PZConfiguratorProps) => {
  const handleValueChange = useCallback(
    ({ zId, pId, value: v }: MatrixValue) => {
      if (!value || !matrix) {
        return;
      }
      value[zId][pId] = v;
      const zIndex = zones.findIndex((z) => z.uuid === zId);
      const pIndex = partitions.findIndex((p) => p.uuid === pId);
      matrix[zIndex][pIndex].value = v;
      onChange({ zId, pId, value: v });
    },
    [value, matrix, zones, partitions, onChange]
  );

  const handleAllValueChange = useCallback(
    (v: MatrixValue[][]) => {
      if (!value || !matrix || !onAllChange) {
        return;
      }
      matrix.forEach((arr, i) =>
        arr.forEach(({ pId, zId, value: val }, j) => {
          value[zId][pId] = val;
          matrix[i][j].value = val;
        })
      );
      onAllChange(v);
    },
    [value, matrix, onAllChange]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setMatrix(zones.map((z) => partitions.map((p) => ({ zId: z.uuid, pId: p.uuid, value: value[z.uuid][p.uuid] }))));
  }, [value, zones, partitions, switchPartitionsAndZones, setMatrix]);

  if (!matrix) {
    return null;
  }

  return view === "grid" ? (
    <>
      <TableView
        value={matrix}
        saved={saved}
        zones={zones}
        partitions={partitions}
        switchPartitionsAndZones={switchPartitionsAndZones}
        onChange={handleValueChange}
        onAllChange={handleAllValueChange}
        disabled={disabled}
      />
    </>
  ) : (
    <RowView
      value={matrix}
      saved={saved}
      zones={zones}
      partitions={partitions}
      switchPartitionsAndZones={switchPartitionsAndZones}
      onChange={handleValueChange}
      onAllChange={handleAllValueChange}
      disabled={disabled}
    />
  );
};

export default PZConfigurator;

export const BootstrapTooltip = styled(({ className, ...props }: TooltipProps & { backgroundcolor?: string }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ backgroundcolor }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: backgroundcolor,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: backgroundcolor,
  },
}));

function isDisplayZoneValue(zone: DisplayZoneValue | Partition): zone is DisplayZoneValue {
  return (zone as DisplayZoneValue).behavior !== undefined;
}

const OptionalBoardLabel = ({ value, sx }: { value: Partition | DisplayZoneValue; sx?: SxProps }) => {
  const isZone = isDisplayZoneValue(value);

  if (!isZone) {
    return null;
  }

  const { board } = value;

  return (
    <Typography
      component="span"
      variant="caption"
      sx={[
        {
          position: "absolute",
          bottom: -2,
          right: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {board === undefined ? "" : board === 0 ? "(master)" : `(slave${board})`}
    </Typography>
  );
};

const OptionalPartitionIcon = ({ value, sx }: { value: Partition | DisplayZoneValue; sx?: SxProps }) => {
  const isZone = isDisplayZoneValue(value);
  const theme = useTheme();

  if (isZone) {
    return null;
  }

  return value.home ? (
    <BootstrapTooltip title={<FormattedMessage defaultMessage={"Stay mode"} />} backgroundcolor={theme.palette.text.primary}>
      <SvgIcon sx={sx}>
        <StayModeIcon width={20} height={20} color={theme.palette.text.primary} />
      </SvgIcon>
    </BootstrapTooltip>
  ) : (
    <></>
  );
};

const OptionalZoneIcon = ({
  value,
  sx,
  badgeProps: _badgeProps,
  currentBackgroundColor,
}: {
  value: Partition | DisplayZoneValue;
  sx?: SxProps;
  badgeProps: BadgeProps;
  currentBackgroundColor?: string;
}) => {
  const isZone = isDisplayZoneValue(value);
  const theme = useTheme();
  const { sx: badgeSx, ...badgeProps } = _badgeProps;

  if (!isZone) {
    return null;
  }
  const behavior = behaviors.find((behavior) => behavior.value === value?.behavior?.toString(2));
  const trigger = triggers.find((trigger) => trigger.value === value?.trigger);

  const icon = value.output ? trigger?.icon : behavior?.icon;
  const label = value.output ? trigger?.label : behavior?.label;
  const color = value.output ? trigger?.color : behavior?.color;

  return (
    <BootstrapTooltip title={label || ""} backgroundcolor={color}>
      <Badge
        badgeContent={`p${value.port}`}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={[
          {
            "& .MuiBadge-badge": {
              left: 4,
              top: 12,
              border: `2px solid ${currentBackgroundColor || theme.palette.background.paper}`,
              padding: "0 4px",
              backgroundColor: color,
              color: color && theme.palette.getContrastText(color),
            },
          },
          ...(Array.isArray(badgeSx) ? badgeSx : [badgeSx]),
        ]}
        {...badgeProps}
      >
        <SvgIcon sx={[{ color: color, m: 1 }, ...(Array.isArray(sx) ? sx : [sx])]}>{icon}</SvgIcon>
      </Badge>
    </BootstrapTooltip>
  );
};

const RowView = ({ value, saved, zones, partitions, switchPartitionsAndZones, disabled, onChange, onAllChange }: ViewProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { background: editedBackground, text: editedText } = useEditedColor();

  const sendChanged = useCallback(
    (zIndex: number, pIndex: number) => {
      onChange(value[zIndex][pIndex]);
    },
    [value, onChange]
  );

  const sendAllChanged = useCallback(() => {
    if (!onAllChange) {
      return;
    }
    onAllChange(value);
  }, [value, onAllChange]);

  if (!value) {
    return null;
  }

  return (
    <Grid container spacing={1} sx={{ padding: 2 }}>
      {switchPartitionsAndZones &&
        partitions.length === value[0]?.length &&
        partitions.map((row, rowIndex) => {
          return (
            <React.Fragment key={`row-${row.uuid}`}>
              <Typography
                component="span"
                variant="h6"
                sx={{ textAlign: "right", ...(row.deleted ? { borderRadius: 4, paddingX: 1, backgroundColor: editedBackground, color: editedText } : {}) }}
              >
                {[
                  row.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | `,
                  row.code,
                  row.name || formatMessage({ defaultMessage: "partition" }),
                ].join(" ")}
              </Typography>
              <SelectedZones
                label={formatMessage({ defaultMessage: "Choose zone" })}
                items={zones}
                value={value.map((z) => z[rowIndex].value)}
                saved={!saved ? null : zones.map((zone) => saved[zone.uuid]).map((zoneValue) => zoneValue?.charAt(rowIndex) === "1" || false)}
                onChange={(v: boolean, index: number) => {
                  value[index][rowIndex].value = v;
                  sendChanged(index, rowIndex);
                }}
                onAllChange={(v: boolean[]) => {
                  value.forEach((col, colIndex) => {
                    col[rowIndex].value = v[colIndex];
                  });
                  sendAllChanged();
                }}
                disabled={disabled}
              />
            </React.Fragment>
          );
        })}
      {!switchPartitionsAndZones &&
        zones.length === value.length &&
        zones.map((zone, zoneIndex) => {
          const behavior = behaviors.find((behavior) => behavior.value === zone?.behavior?.toString(2));
          const trigger = triggers.find((trigger) => trigger.value === zone?.trigger);

          return (
            <Grid
              item
              container
              key={`row-${zone.uuid}`}
              sx={{
                borderTop: zoneIndex > 0 ? `1px solid ${theme.palette.divider}` : "none",
                marginRight: 1,
                alignSelf: "flex-start",
                justifySelf: "flex-start",
              }}
            >
              <Grid item xs={12}>
                <Box sx={{ display: "flex" }}>
                  <ZoneChip
                    zoneColor={behavior?.color || trigger?.color}
                    icon={behavior?.icon || trigger?.icon}
                    zone={zone}
                    edited={zone.deleted || (!!saved && !Object.keys(saved).includes(zone.uuid))}
                    titleView={true}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SelectedPartitions
                  label={formatMessage({ defaultMessage: "Choose partition" })}
                  items={partitions}
                  value={value[zoneIndex].map((i) => i.value)}
                  saved={!saved ? null : saved[zone.uuid]?.split("").map((partitionValue) => partitionValue === "1") || null}
                  onChange={(v: boolean, index: number) => {
                    value[zoneIndex][index].value = v;
                    sendChanged(zoneIndex, index);
                  }}
                  onAllChange={(v: boolean[]) => {
                    value[zoneIndex].forEach((col, colIndex) => {
                      col.value = v[colIndex];
                    });
                    sendAllChanged();
                  }}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          );
        })}
    </Grid>
  );
};

const TableColumnLabel = ({ children, sx }: { children: ReactNode; sx?: SxProps }) => {
  return (
    <Box sx={[{ height: "200px", width: "60px" }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <div
        style={{
          width: "200px",
          height: "60px",
          lineHeight: "30px",
          transformOrigin: "left top",
          transform: "translateY(200px) rotate(-90deg)",
          textAlign: "left",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {children}
      </div>
    </Box>
  );
};

const TableView = ({ value, saved, zones, partitions, switchPartitionsAndZones, disabled, onChange, onAllChange }: ViewProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();

  const sendChanged = useCallback(
    (zIndex: number, pIndex: number) => {
      onChange(value[zIndex][pIndex]);
    },
    [value, onChange]
  );

  const sendAllChanged = useCallback(() => {
    if (!onAllChange) {
      return;
    }
    onAllChange(value);
  }, [value, onAllChange]);

  if (!value) {
    return null;
  }

  const columns = switchPartitionsAndZones ? zones : partitions;
  const rows = switchPartitionsAndZones ? partitions : zones;

  return (
    <div
      style={{
        width: "100%",
        /*maxHeight: "40vh",*/ overflow: "auto",
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      }}
    >
      <table style={{ borderSpacing: 0 }}>
        <tbody>
          <tr>
            <td></td>
            {rows && rows.length > 0 && (
              <th>
                <div style={{ height: "200px", width: "59px", marginBottom: "-2px" }}>
                  <TableColumnLabel sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
                    <span
                      style={{
                        marginLeft: theme.spacing(1),
                        maxHeight: "60px",
                      }}
                    >
                      {formatMessage({ defaultMessage: "Select all" })}
                    </span>
                  </TableColumnLabel>
                </div>
              </th>
            )}
            {columns.map((col, colIndex) => {
              const title = [
                col.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | `,
                col.code,
                col.name || (switchPartitionsAndZones ? formatMessage({ defaultMessage: "zone" }) : formatMessage({ defaultMessage: "partition" })),
              ]
                .filter((i) => !!i)
                .join(" ");
              const edited = saved ? (switchPartitionsAndZones && !saved[col.uuid]) || col.deleted : false;
              return (
                <th
                  key={`title-part-${col.uuid}`}
                  style={{
                    ...(colIndex % 2 === 1 &&
                      !(switchPartitionsAndZones && (col as DisplayZoneValue).disabled) && { backgroundColor: lighten(theme.palette.primary.main, 0.8) }),
                    ...(colIndex === columns.length - 1 && {
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }),
                  }}
                  className={switchPartitionsAndZones && (col as DisplayZoneValue).disabled ? "disabledrow" : ""}
                >
                  <TableColumnLabel>
                    <Tooltip title={title}>
                      <span
                        style={{
                          marginLeft: theme.spacing(1),
                          maxHeight: "60px",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          ...(edited
                            ? {
                                borderRadius: theme.spacing(2),
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                                backgroundColor: editedBackground,
                                color: editedText,
                              }
                            : {}),
                        }}
                      >
                        {title}
                      </span>
                    </Tooltip>
                    <OptionalZoneIcon
                      value={col}
                      badgeProps={{ sx: { transform: "rotate(90deg)" } }}
                      currentBackgroundColor={
                        colIndex % 2 === 1 && !(switchPartitionsAndZones && (col as DisplayZoneValue).disabled)
                          ? lighten(theme.palette.primary.main, 0.8)
                          : undefined
                      }
                    />
                    <OptionalPartitionIcon value={col} sx={{ transform: "rotate(90deg)", mr: 1 }} />
                    <OptionalBoardLabel value={col} sx={{ right: 50 }} />
                  </TableColumnLabel>
                </th>
              );
            })}
          </tr>
          {rows.map((row, rowIndex) => {
            const title = [
              row.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | `,
              row.code,
              row.name || (switchPartitionsAndZones ? formatMessage({ defaultMessage: "partition" }) : formatMessage({ defaultMessage: "zone" })),
            ].join(" ");
            const edited = saved ? (!switchPartitionsAndZones && !saved[row.uuid]) || row.deleted : false;
            return (
              <tr key={`zone-row-${row.uuid}`}>
                <th
                  style={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                    ...(rowIndex === rows.length - 1 && {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }),
                  }}
                  className={!switchPartitionsAndZones && (row as DisplayZoneValue).disabled ? "disabledrow" : ""}
                >
                  <div
                    style={{
                      maxWidth: 200,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      maxHeight: "60px",
                      lineHeight: "30px",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <OptionalZoneIcon
                      value={row}
                      badgeProps={{
                        sx: {
                          "& .MuiBadge-badge": {
                            right: 4,
                            left: "unset",
                          },
                        },
                        anchorOrigin: { vertical: "bottom", horizontal: "right" },
                      }}
                    />
                    <Tooltip title={title}>
                      <span
                        style={{
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          marginLeft: 8,
                          ...(edited
                            ? {
                                borderRadius: theme.spacing(2),
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                                backgroundColor: editedBackground,
                                color: editedText,
                              }
                            : {}),
                        }}
                      >
                        {title}
                      </span>
                    </Tooltip>
                    <OptionalBoardLabel value={row} />
                    <OptionalPartitionIcon value={row} sx={{ ml: 1 }} />
                  </div>
                </th>
                <td
                  style={{
                    textAlign: "center",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: "transparent",
                    borderRight: `1px solid ${theme.palette.divider}`,
                    ...(rowIndex === rows.length - 1 && {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }),
                  }}
                >
                  {switchPartitionsAndZones ? (
                    <Checkbox
                      checked={value.reduce((checked: boolean, column) => {
                        if (!column[rowIndex]?.value) {
                          checked = false;
                        }
                        return checked;
                      }, true)}
                      indeterminate={(() => {
                        let indeterminate = false;
                        for (let i = 0; i < value.length - 1; i++) {
                          if (value[i][rowIndex]?.value !== value[i + 1][rowIndex]?.value) {
                            indeterminate = true;
                          }
                        }
                        return indeterminate;
                      })()}
                      onChange={(e, checked) => {
                        value.forEach((column) => {
                          column[rowIndex].value = checked;
                        });
                        sendAllChanged();
                      }}
                      sx={{ margin: 0 }}
                      color="secondary"
                      disabled={disabled}
                    />
                  ) : (
                    <Checkbox
                      checked={value[rowIndex].reduce((checked: boolean, row) => {
                        if (!row.value) {
                          checked = false;
                        }
                        return checked;
                      }, true)}
                      indeterminate={(() => {
                        let indeterminate = false;
                        for (let i = 0; i < value[rowIndex].length - 1; i++) {
                          if (value[rowIndex][i].value !== value[rowIndex][i + 1].value) {
                            indeterminate = true;
                          }
                        }
                        return indeterminate;
                      })()}
                      onChange={(e, checked) => {
                        value[rowIndex].forEach((row) => {
                          row.value = checked;
                        });
                        sendAllChanged();
                      }}
                      sx={{ margin: 0 }}
                      color="secondary"
                    />
                  )}
                </td>
                {columns.map((col, colIndex) => (
                  <td
                    key={`value-${row.uuid}-${col.uuid}`}
                    style={{
                      textAlign: "center",
                      borderTop: `1px solid ${theme.palette.divider}`,
                      backgroundColor: colIndex % 2 === 1 ? lighten(theme.palette.primary.main, 0.8) : "transparent",
                      ...(colIndex === columns.length - 1 && { borderRight: `1px solid ${theme.palette.divider}` }),
                      ...(rowIndex === rows.length - 1 && {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }),
                    }}
                  >
                    {(switchPartitionsAndZones ? value[colIndex][rowIndex] : value[rowIndex][colIndex]) && (
                      <TableCheckbox
                        checked={(switchPartitionsAndZones ? value[colIndex][rowIndex].value : value[rowIndex][colIndex].value) || false}
                        onChange={(e, checked) => {
                          if (switchPartitionsAndZones) {
                            value[colIndex][rowIndex].value = checked;
                            sendChanged(colIndex, rowIndex);
                          } else {
                            value[rowIndex][colIndex].value = checked;
                            sendChanged(rowIndex, colIndex);
                          }
                        }}
                        className={
                          saved &&
                          (switchPartitionsAndZones
                            ? ((saved[col.uuid] || "").charAt(rowIndex) === "1") !== value[colIndex][rowIndex].value
                            : ((saved[row.uuid] || "").charAt(colIndex) === "1") !== value[rowIndex][colIndex].value)
                            ? editedStyle.background
                            : ""
                        }
                        disabled={disabled}
                      />
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TableCheckbox = ({ checked, className, disabled, sx, onChange }: CheckboxProps) => {
  const [value, setValue] = useState(checked);

  useEffect(() => {
    setValue(checked);
  }, [checked]);

  return (
    <Checkbox
      checked={value}
      onChange={(e, checked) => {
        setValue(checked);
        if (onChange) {
          onChange(e, checked);
        }
      }}
      sx={{ margin: 0, ...sx }}
      className={className}
      disabled={disabled}
    />
  );
};
